import React, { useState } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

export default function OpportunityDetailModal({ opportunity, onClose, onExpressInterest, onSave }) {
  const [activeTab, setActiveTab] = useState('overview');

  if (!opportunity) return null;

  const tabs = ['overview', 'founder', 'financials', 'team'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(27,42,94,0.7)' }} onClick={onClose}>
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={e => e?.stopPropagation()}
      >
        {/* Header image */}
        <div className="relative h-48 md:h-56 flex-shrink-0 overflow-hidden">
          <Image src={opportunity?.image} alt={opportunity?.imageAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(27,42,94,0.8), transparent)' }} />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={18} color="white" />
          </button>
          {opportunity?.isWinPick && (
            <div className="absolute top-4 left-4 flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold" style={{ background: 'var(--color-accent)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>
              <Icon name="Star" size={12} color="var(--color-primary)" />
              WIN Pick
            </div>
          )}
          <div className="absolute bottom-4 left-4 right-4">
            <div className="flex items-end justify-between">
              <div>
                <h2 className="font-heading font-bold text-white text-xl md:text-2xl">{opportunity?.name}</h2>
                <p className="font-caption text-white/80 text-sm" style={{ fontFamily: 'var(--font-caption)' }}>{opportunity?.sector} · {opportunity?.location}</p>
              </div>
              {opportunity?.isWomenLed && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold" style={{ background: 'rgba(133,113,77,0.9)', color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>
                  <Icon name="Crown" size={12} color="var(--color-primary)" />
                  Women-Led
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b overflow-x-auto flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
          {tabs?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 md:px-6 py-3 font-caption text-sm font-medium capitalize whitespace-nowrap transition-colors border-b-2 ${activeTab === tab ? 'border-yellow-500 text-yellow-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
              style={{ fontFamily: 'var(--font-caption)' }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {activeTab === 'overview' && (
            <div className="space-y-5">
              <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-foreground)' }}>{opportunity?.description}</p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: 'Min. Investment', value: `$${opportunity?.minInvestment?.toLocaleString()}` },
                  { label: 'Projected ROI', value: `${opportunity?.projectedROI}%` },
                  { label: 'Risk Level', value: opportunity?.riskLevel },
                  { label: 'Funding Stage', value: opportunity?.fundingStage },
                ]?.map(stat => (
                  <div key={stat?.label} className="p-3 rounded-lg" style={{ background: 'var(--color-surface)' }}>
                    <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{stat?.label}</p>
                    <p className="font-data font-bold text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{stat?.value}</p>
                  </div>
                ))}
              </div>

              <div>
                <h4 className="font-heading font-semibold mb-2" style={{ color: 'var(--color-foreground)' }}>Funding Progress</h4>
                <div className="flex justify-between mb-1">
                  <span className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>${opportunity?.raisedAmount?.toLocaleString()} raised</span>
                  <span className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{opportunity?.fundingProgress}% of ${opportunity?.targetAmount?.toLocaleString()}</span>
                </div>
                <div className="h-3 rounded-full bg-gray-100 overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${opportunity?.fundingProgress}%`, background: 'var(--color-accent)' }} />
                </div>
              </div>

              <div>
                <h4 className="font-heading font-semibold mb-2" style={{ color: 'var(--color-foreground)' }}>Key Highlights</h4>
                <ul className="space-y-2">
                  {opportunity?.highlights?.map((h, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                      <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{h}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'founder' && (
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0">
                  <Image src={opportunity?.founder?.avatar} alt={opportunity?.founder?.avatarAlt} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-lg" style={{ color: 'var(--color-foreground)' }}>{opportunity?.founder?.name}</h3>
                  <p className="font-caption text-sm" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{opportunity?.founder?.title}</p>
                  <div className="flex gap-2 mt-2">
                    {opportunity?.founder?.linkedin && (
                      <span className="font-caption text-xs px-2 py-1 rounded" style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>LinkedIn</span>
                    )}
                  </div>
                </div>
              </div>
              <p className="font-body text-sm leading-relaxed" style={{ color: 'var(--color-foreground)' }}>{opportunity?.founder?.bio}</p>
              <div className="grid grid-cols-2 gap-3">
                {opportunity?.founder?.achievements?.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 p-3 rounded-lg" style={{ background: 'var(--color-surface)' }}>
                    <Icon name="Award" size={16} color="var(--color-accent)" />
                    <span className="font-caption text-sm" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'financials' && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {opportunity?.financials?.map((f, i) => (
                  <div key={i} className="p-4 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
                    <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{f?.label}</p>
                    <p className="font-data font-bold text-lg" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-data)' }}>{f?.value}</p>
                    {f?.change && (
                      <p className="font-caption text-xs mt-1" style={{ color: f?.change > 0 ? 'var(--color-success)' : 'var(--color-error)', fontFamily: 'var(--font-caption)' }}>
                        {f?.change > 0 ? '+' : ''}{f?.change}% YoY
                      </p>
                    )}
                  </div>
                ))}
              </div>
              <div className="p-4 rounded-lg" style={{ background: 'rgba(133,113,77,0.08)', border: '1px solid rgba(133,113,77,0.3)' }}>
                <div className="flex items-start gap-2">
                  <Icon name="FileText" size={16} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-caption text-sm font-medium" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>Pitch Deck Available</p>
                    <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>Express interest to access the full pitch deck and financial projections</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'team' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {opportunity?.team?.map((member, i) => (
                <div key={i} className="flex items-center gap-3 p-3 rounded-lg border" style={{ borderColor: 'var(--color-border)' }}>
                  <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                    <Image src={member?.avatar} alt={member?.avatarAlt} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <p className="font-caption text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}>{member?.name}</p>
                    <p className="font-caption text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{member?.role}</p>
                    <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{member?.background}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="p-4 border-t flex items-center gap-3 flex-shrink-0" style={{ borderColor: 'var(--color-border)' }}>
          <Button variant="default" onClick={() => onExpressInterest(opportunity)} className="flex-1">
            Express Interest
          </Button>
          <Button variant="outline" onClick={() => onSave(opportunity?.id)}>
            <Icon name={opportunity?.isSaved ? 'BookmarkCheck' : 'Bookmark'} size={16} color="currentColor" />
          </Button>
        </div>
      </div>
    </div>
  );
}